import {
  MessagesSquare,
  CalendarDays,
  Activity,
  ChevronDown,
  LayoutDashboard,
  LogOut,
  type LucideIcon,
  Menu,
  ShieldCheck,
  User,
} from "lucide-react"
import { useState } from "react"
import { Link, NavLink, Outlet, ScrollRestoration, useNavigate } from "react-router-dom"
import { UserRole } from "~/__generated__/graphql"
import { useLogout } from "~/auth/use-logout"
import { useUiAccess } from "~/auth/use-ui-access"
import { useViewer } from "~/auth/use-viewer"
import { AppConfig } from "~/common/app-config"
import { cn } from "~/common/cn"
import {
  experienceProfilePath,
  calendarPath,
  operationsPath,
  dashboardPath,
  internalAdminDashboardPath,
  profilePath,
  rootPath,
} from "~/common/paths"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/ui/dropdown-menu"
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "~/ui/sheet"

const sidebarItemStyles =
  "mx-3 flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-primary cursor-pointer"

const SidebarLink = ({
  text,
  to,
  icon: Icon,
  onNavigate,
}: {
  text: string
  to: string
  icon: LucideIcon
  onNavigate?: () => void
}) => (
  <li>
    <NavLink
      to={to}
      onClick={onNavigate}
      className={({ isActive }) => {
        return cn(sidebarItemStyles, { "bg-accent text-primary": isActive })
      }}
    >
      <Icon className="size-5" aria-hidden />
      <div className="">{text}</div>
    </NavLink>
  </li>
)

export const SidebarLinks = ({ onNavigate }: { onNavigate?: () => void }) => {
  const { uiAccess } = useUiAccess()

  return (
    <nav className="mt-6">
      <ul className="flex list-none flex-col gap-1">
        <SidebarLink
          text={"Dashboard"}
          to={dashboardPath({})}
          icon={LayoutDashboard}
          onNavigate={onNavigate}
        />
        <SidebarLink
          text="My experience"
          to={experienceProfilePath({})}
          icon={User}
          onNavigate={onNavigate}
        />
        <SidebarLink
          text="Calendar"
          to={calendarPath({})}
          icon={CalendarDays}
          onNavigate={onNavigate}
        />
        <SidebarLink
          text="Usage & reminders"
          to={operationsPath({})}
          icon={Activity}
          onNavigate={onNavigate}
        />
        {uiAccess?.canInternalAdmin.value && (
          <SidebarLink
            text={"Admin"}
            to={internalAdminDashboardPath({})}
            icon={ShieldCheck}
            onNavigate={onNavigate}
          />
        )}
      </ul>
    </nav>
  )
}

const userRoleLabel = (role: UserRole): string => {
  switch (role) {
    case "SYSTEM_ADMIN":
      return "Admin"
    case "DEFAULT":
      return "Member"
    default:
      throw role satisfies never
  }
}

const UserMenu = ({ onNavigate }: { onNavigate?: () => void }) => {
  const { viewer } = useViewer()
  const logout = useLogout()
  const navigate = useNavigate()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          "flex w-full items-center gap-3 border-t border-border px-5 py-3 text-left",
          "hover:bg-accent focus:outline-none focus-visible:bg-accent"
        )}
      >
        <User className="size-5 shrink-0 text-muted-foreground" aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-foreground">{viewer.firstName}</div>
          {viewer.userRole && (
            <div className="truncate text-xs text-muted-foreground">
              {userRoleLabel(viewer.userRole)}
            </div>
          )}
        </div>
        <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        side="right"
        sideOffset={8}
        collisionPadding={8}
        className="w-[160px]"
      >
        <DropdownMenuItem
          onSelect={() => {
            navigate(profilePath({}))
            onNavigate?.()
          }}
        >
          <User className="size-4" aria-hidden />
          Profile
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => {
            logout()
            onNavigate?.()
          }}
        >
          <LogOut className="size-4" aria-hidden />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const SidebarContents = ({ onNavigate }: { onNavigate?: () => void }) => (
  <div className="flex h-full flex-col justify-between gap-2 bg-card">
    <div>
      <Link
        to={rootPath({})}
        onClick={onNavigate}
        className="flex items-center gap-3 px-6 pt-8 pb-3 text-lg font-semibold leading-tight tracking-tight"
      >
        <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
          <MessagesSquare className="size-5" aria-hidden />
        </span>
        {AppConfig.app_name}
      </Link>

      <p className="px-6 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
        A Tanooki Labs workspace
      </p>
      <SidebarLinks onNavigate={onNavigate} />
    </div>
    <UserMenu onNavigate={onNavigate} />
  </div>
)

export const SidebarLayout = () => {
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const closeMobileNav = () => setMobileNavOpen(false)

  return (
    <div className="flex min-h-screen flex-1 grow flex-col md:flex-row">
      <header className="flex items-center justify-between border-b border-border bg-card px-4 py-3 md:hidden">
        <Link
          to={rootPath({})}
          className="flex items-center gap-2 text-base font-semibold tracking-tight"
        >
          <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <MessagesSquare className="size-5" aria-hidden />
          </span>
          {AppConfig.app_name}
        </Link>
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetTrigger className="-mr-2 rounded p-2 hover:bg-accent" aria-label="Open navigation">
            <Menu className="size-5" aria-hidden />
          </SheetTrigger>
          <SheetContent side="left" className="w-[240px] p-0">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <SidebarContents onNavigate={closeMobileNav} />
          </SheetContent>
        </Sheet>
      </header>

      <div className="sticky top-0 hidden h-screen w-[232px] shrink-0 border-r md:block">
        <SidebarContents />
      </div>

      <div className="flex max-w-full flex-1 flex-col overflow-hidden">
        <Outlet />
        <ScrollRestoration />
      </div>
    </div>
  )
}
